import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Membership.scss";
import crossIcon from "../../../../assets/icons/cross.png";
import groupIcon from "../../../../assets/icons/group.png";

const Membership = () => {
    const navigate = useNavigate();
    // Refs for scroll animations
    const benefitsRef = useRef(null);
    
    // Animation states
    const [benefitsVisible, setBenefitsVisible] = useState(false);

    // Intersection Observer for scroll animations
    useEffect(() => {
        const observerOptions = {
            threshold: 0.2,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === benefitsRef.current && entry.isIntersecting) {
                    setBenefitsVisible(true);
                }
            });
        }, observerOptions);

        if (benefitsRef.current) observer.observe(benefitsRef.current);

        return () => {
            if (benefitsRef.current) observer.unobserve(benefitsRef.current);
        };
    }, []);

    return (
        <div className="membership-page">
            {/* Hero Section */}
            <div className="membership-page__hero">
                <div className="hero-content">
                    <div className="hero-text">
                        <h1>Why join the<br />Sts. Vartanantz Church<br />Ladies Guild</h1>
                    </div>
                </div>
                <div className="hero-decoration">
                    <div className="decoration-cross"></div>
                </div>
            </div>

            <div className="membership-page__container">
                <div className="membership-page__content">
                    {/* Introduction Section */}
                    <section className="intro-section">
                        <p className="intro-text">
                            Joining the Ladies Guild means becoming part of a vibrant community dedicated to faith, 
                            service, and the preservation of our Armenian heritage. Together, we create lasting 
                            impact in our church and community.
                        </p>
                    </section>

                    {/* Benefits Section */}
                    <section ref={benefitsRef} className={`benefits-section ${benefitsVisible ? 'animate-in' : ''}`}>
                        <h2 className="benefits-title">Benefits of Membership</h2>
                        <div className="benefits-grid">
                            <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.2s' }}>
                                <div className="benefit-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                                    </svg>
                                </div>
                                <div className="benefit-content">
                                    <h3>Strengthen Community Bonds</h3>
                                    <p>Strengthen your bond with the Church and our community through meaningful participation and fellowship.</p>
                                </div>
                            </div>
                            
                            <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.4s' }}>
                                <div className="benefit-icon">
                                    <img src={crossIcon} alt="Faith" />
                                </div>
                                <div className="benefit-content">
                                    <h3>Support Our Faith</h3>
                                    <p>Locally and regionally support and maintain our Armenian Apostolic doctrines through dedicated service.</p>
                                </div>
                            </div>
                            
                            <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.6s' }}>
                                <div className="benefit-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                    </svg>
                                </div>
                                <div className="benefit-content">
                                    <h3>Cultural Heritage</h3>
                                    <p>Expand your knowledge of our Armenian traditions and cultural heritage through events and education.</p>
                                </div>
                            </div>
                            
                            <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '0.8s' }}>
                                <div className="benefit-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                                    </svg>
                                </div>
                                <div className="benefit-content">
                                    <h3>Empower Our Youth</h3>
                                    <p>Create an environment for our youth to perpetuate growth of the Armenian Apostolic Church in America.</p>
                                </div>
                            </div>
                            
                            <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '1.0s' }}>
                                <div className="benefit-icon">
                                    <img src={groupIcon} alt="Community" />
                                </div>
                                <div className="benefit-content">
                                    <h3>Build Connections</h3>
                                    <p>Socially interact with others who share similar values and goals in a welcoming environment.</p>
                                </div>
                            </div>
                            <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '1.2s' }}>
                                <div className="benefit-icon">
                                    <svg viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                                        <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <div className="benefit-content">
                                    <h3>Spiritual Enrichment</h3>
                                    <p>Deepen your spiritual journey through prayer, fellowship, and opportunities to grow in faith alongside other devoted members.</p>
                                </div>
                            </div>


                            {/* <div className={`benefit-card ${benefitsVisible ? 'animate-card' : ''}`} style={{ animationDelay: '1.0s' }}></div> */}
                        </div>

                    </section>

                    {/* Call to Action Section */}
                    <section className="cta-section">
                        <div className="cta-content">
                            <h2>Ready to Join?</h2>
                            <p>
                                Become part of our sisterhood and contribute to the vibrant life of our church community. 
                                Together, we make a difference.
                            </p>
                            <button 
                                className="cta-button"
                                onClick={() => navigate('/', { state: { scrollToContact: true } })}
                            >
                                Contact Us to Learn More
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Membership;
